/*
 * Copyright (C) 2024 Webformat S.r.l.
 * https://www.webformat.com
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { Action, getModule, Module, Mutation, MutationAction, VuexModule } from 'vuex-module-decorators';
import store from '@/store';
import { normalizeQuoteAddress, removeError } from '@/commons/addressNormalizer';
import to from 'await-to-js';
import handleError from '@/commons/errorHandler';
import { decode as decodeShipment, encode as encodeShipment } from '@/commons/shipmentMethodHelpers';
import loyalty from '@/store/modules/loyalty';
import { err } from '@/commons/toast';
import { i18n } from '@/i18n';
import redirectToLocation from '@/commons/redirectManager';
import mse from '@adobe/magento-storefront-events-sdk';
import handleMessage from '@/commons/messageManager';
import {
    assignAddress,
    createMagentoOrder,
    deleteCoupon,
    doApplyCoupon,
    estimateShipping,
    getCoupon,
    getPaymentMethods,
    getSelectedPaymentMethod,
    getShippingMethods,
    removeShippingOptions,
    setPaymentInformation,
    setPaymentMethod,
    setShippingInformation,
    setShippingOption,
    socialLogin,
    updateRequestInvoice,
} from '../api';
import {
    AssignAddressRequest,
    CheckoutStep,
    Coupon,
    EducationSalesConditions,
    EstimateAddress,
    EstimateShippingRequest,
    InvoiceRequest,
    PaymentDescription,
    PaymentInformationRequest,
    PaymentMethod,
    QuoteAddress,
    ShippingCountries,
    ShippingFree,
    ShippingInfoData,
    ShippingMethod,
    ShippingOption,
    ShippingOptions,
    ShippingTimes,
    UpdatePaymentRequest,
} from '../models';
import cart from './cart';
import { blankState, initState } from '../stateinit';
import stripe from '@/store/modules/stripe';

@Module({
    namespaced: true,
    name: 'checkout',
    store,
    dynamic: true,
})
class CheckoutModule extends VuexModule {
    public billingAddress: QuoteAddress = initState('billingAddress');
    public shippingAddress: QuoteAddress = initState('shippingAddress');
    public email: string = initState('checkoutEmail');
    public coupon: Coupon = initState('coupon');
    public selectedPayment: string = initState('selectedPayment');
    public selectedShipment: string = initState('selectedShipment');
    public shippingMethods: ShippingMethod[] = initState('shippingMethods');
    public shippingOptions: ShippingOptions = initState('shippingOptions');
    public shippingFree: ShippingFree = initState('shippingFree');
    public shippingTimes: ShippingTimes = initState('shippingTimes');
    public countries: string[] = initState('shippingCountries');
    public selectedCountry: string = '';
    public paymentMethods: PaymentMethod[] = initState('paymentMethods');
    public paymentDescriptions: PaymentDescription[] = initState('paymentDescriptions');
    public steps: CheckoutStep[] = initState('steps');
    public orderId: number = 0;
    public requestInvoice: InvoiceRequest = initState('requestInvoice');
    public additionalData: string[] = [];
    public editShippingAddress: boolean = false;
    public billingSameAsShipping: boolean = true;
    public placingOrder: boolean = false;
    public selectedAddressId: any = {
        shipping: 0,
        billing: 0,
    };
    public confirmStep: boolean = false;
    public agreements = {
        privacy: false,
        sales: false,
        education: false,
    };
    public checkReview = false;
    public messages = initState('checkoutMessages');
    public lastOrder = initState('lastOrderData');
    public orderPlaced: boolean = false;
    public loyalty = initState('loyalty');
    public shippingCountries: ShippingCountries = initState('shippingCountries');
    public selectedShippingCountry: string | null = null;
    public estimateAddress: EstimateAddress | null = null;
    public isShippingLoading = false;
    public canMakeApplePayPayments = false;
    public educationSalesConditions: EducationSalesConditions = initState('education');

    get currentStep(): CheckoutStep {
        const currentStep = this.steps.find((step: CheckoutStep) => step.active);

        return currentStep as CheckoutStep;
    }

    get stepByName(): any {
        return (name: any) => {
            const currentStep = this.steps.find((step: CheckoutStep) => step.name === name);
            return currentStep as CheckoutStep;
        };
    }

    get hasShippingMethod(): (methodName: string) => boolean {
        return (methodName: string) => {
            const foundSelectedShippingMethod = this.shippingMethods.find((m) => {
                return encodeShipment(m) === methodName;
            });

            return !!foundSelectedShippingMethod;
        };
    }

    get hasPaymentMethod(): (methodName: string) => boolean {
        return (methodName: string) => {
            const found = this.paymentMethods.find((m) => m.code === methodName);

            return !!found;
        };
    }

    get defaultShippingAddress(): QuoteAddress {
        return this.shippingAddress;
    }

    get selectedShippingMethod(): ShippingMethod {
        const decodedShipment = decodeShipment(this.selectedShipment);

        return this.shippingMethods.find(
            (sm: ShippingMethod) =>
                sm.carrier_code === decodedShipment.carrier_code && sm.method_code === decodedShipment.method_code
        ) as ShippingMethod;
    }

    get selectedShippingMethodOptions(): ShippingOption[] {
        if (
            this.selectedShippingMethod &&
            this.selectedShippingMethod.extension_attributes &&
            this.selectedShippingMethod.extension_attributes.options
        ) {
            const customOrder: any = {
                skip: 1,
                notice: 2,
            };

            if (loyalty.loyaltyBenefitsSkip !== -1) {
                this.selectedShippingMethod.extension_attributes.options =
                    this.selectedShippingMethod.extension_attributes.options.filter((option) => {
                        return option.code !== 'skip';
                    });
            }

            return this.selectedShippingMethod.extension_attributes.options.sort((a, b) => {
                return customOrder[a.code] - customOrder[b.code];
            });
        }

        return [];
    }

    get selectedShippingMethodTimes(): ShippingTimes {
        if (
            this.selectedShippingMethod &&
            this.selectedShippingMethod.extension_attributes &&
            this.selectedShippingMethod.extension_attributes.times
        ) {
            return this.selectedShippingMethod.extension_attributes.times;
        }

        return initState('shippingTimes');
    }

    get selectedShippingMethodAsString(): string {
        return this.selectedShipment;
    }

    get selectedPaymentMethod(): PaymentMethod {
        return this.paymentMethods.find((pm: PaymentMethod) => pm.code === this.selectedPayment) as PaymentMethod;
    }

    get selectedShippingNotice(): boolean {
        return this.shippingOptions.notice.selected;
    }

    get selectedShippingSkip(): boolean {
        return this.shippingOptions.skip.selected;
    }

    get agreementsAccepted(): boolean {
        return (
            this.agreements.privacy &&
            this.agreements.sales &&
            (this.showEducationSalesCondition ? this.agreements.education : true)
        );
    }

    get isCheckReview() {
        return this.checkReview;
    }

    get customerLoyaltyGroupLabel() {
        return this.loyalty.customer_current_loyalty_group.group_label;
    }

    get cartContainsCourseOnSite() {
        return !!cart.cart.items.find((item) => (item.pu_course ? item.pu_course === 'onsite' : false));
    }

    get showEducationSalesCondition() {
        return this.educationSalesConditions.checkbox_enabled && this.cartContainsCourseOnSite;
    }

    get isOrderplaced() {
        return this.orderPlaced;
    }

    @Mutation
    public setCheckReview(value: boolean) {
        this.checkReview = value;
    }

    @Mutation
    public setAdditionalData(value: any) {
        this.additionalData = value;
    }

    @Mutation
    public setCouponCode(value: string) {
        this.coupon.coupon = value;
    }

    @Mutation
    public setCoupon(value: Coupon) {
        this.coupon = value;
    }

    @Mutation
    public setOrderId(value: number) {
        this.orderId = value;
    }

    @Mutation
    public setEmail(value: string) {
        this.email = value;
    }

    @Mutation
    public setBillingAddress(address: QuoteAddress) {
        this.billingAddress = normalizeQuoteAddress(address);
        stripe.updateStripeBillingAddress(address);
    }

    @Mutation
    public setShippingAddress(address: QuoteAddress) {
        this.shippingAddress = normalizeQuoteAddress(address);
    }

    @Mutation
    public setSelectedShipment(value: string) {
        this.selectedShipment = value;
    }

    @Mutation
    public setSelectedCountry(value: string) {
        this.selectedCountry = value;
    }

    @Mutation
    public setSelectedPayment(value: string) {
        this.selectedPayment = value;
    }

    @Mutation
    public flagEditShippingAddress(flag: boolean) {
        this.editShippingAddress = flag;
    }

    @Mutation
    public flagPlacingOrder(flag: boolean) {
        this.placingOrder = flag;
    }

    @Mutation
    public flagOrderPlaced(flag: boolean) {
        this.orderPlaced = flag;
    }

    @Mutation
    public setBillingSameAsShipping(flag: boolean) {
        this.billingSameAsShipping = flag;

        if (flag) {
            if (this.shippingAddress.customer_address_id) {
                this.billingAddress.customer_address_id = this.shippingAddress.customer_address_id;
                this.selectedAddressId.billing = this.shippingAddress.customer_address_id;
            }
        }
    }

    @Mutation
    public setRequestInvoice(flag: boolean) {
        this.requestInvoice.requested = flag;
        // le mutations sono sempre sincrone!
        // await updateRequestInvoice(flag);
        // await cart.getTotals();
    }

    @Mutation
    public setRequestInvoiceNovat(val: number) {
        this.requestInvoice.novatrequested = val;
    }

    @Mutation
    public setConfirmStep(value: boolean) {
        this.confirmStep = value;
    }

    @Mutation
    public flagStep(payload: { flag: boolean; type: string; stepName: string }) {
        const stepIndex = this.steps.findIndex((step: CheckoutStep) => step.name === payload.stepName);

        switch (payload.type) {
            case 'u':
                this.steps[stepIndex].unlocked = payload.flag;
                break;
            case 'a':
                this.steps[stepIndex].active = payload.flag;
                break;
            default:
                break;
        }
    }

    @Mutation
    public setShippingMethods(methods: ShippingMethod[]) {
        this.shippingMethods = methods;
    }

    @Mutation
    public setPaymentMethods(methods: PaymentMethod[]) {
        this.paymentMethods = methods;
    }

    @Mutation
    public setSelectedAddressId(payload: any) {
        this.selectedAddressId[payload.type] = payload.id;
    }

    @Mutation
    public setCanMakeApplePayPayments(flag: boolean) {
        this.canMakeApplePayPayments = flag;
    }

    @Mutation
    public reset() {
        this.billingAddress = blankState('billingAddress');
        this.shippingAddress = blankState('shippingAddress');
        this.email = blankState('checkoutEmail');
        this.coupon = blankState('coupon');
        this.selectedPayment = blankState('selectedPayment');
        this.selectedShipment = blankState('selectedShipment');
        this.shippingMethods = blankState('shippingMethods');
        this.shippingOptions = blankState('shippingOptions');
        this.shippingTimes = blankState('shippingTimes');
        this.paymentMethods = blankState('paymentMethods');
        this.steps = blankState('steps');
        this.additionalData = [];
        this.editShippingAddress = false;
        this.billingSameAsShipping = true;
        this.placingOrder = false;
        this.confirmStep = false;
    }

    @Mutation
    public resetSteps(currentStep: string) {
        let keepUnlocking = true;

        this.steps.forEach((step: CheckoutStep) => {
            const stepIndex = this.steps.findIndex((s: CheckoutStep) => s.name === step.name);
            this.steps[stepIndex].active = false;
            this.steps[stepIndex].unlocked = false;

            if (keepUnlocking) {
                this.steps[stepIndex].unlocked = true;
            }

            if (step.name === currentStep) {
                this.steps[stepIndex].active = true;
                keepUnlocking = false;
            }
        });

        this.confirmStep = false;
    }

    @Mutation
    public setSelectedShippingCountry(countryId: string) {
        this.selectedShippingCountry = countryId;
    }

    @Mutation
    public setEstimateAddress(estimateAddress: EstimateAddress) {
        this.estimateAddress = estimateAddress;
    }

    @Mutation
    public setIsShippingLoading(isLoading: boolean) {
        this.isShippingLoading = isLoading;
    }

    @Action
    public async changeSelectedShippingOption(payload: { code: string; newStatus: boolean }) {
        cart.flagLoading({ key: 'totals', value: true });

        const data = await to(setShippingOption(payload.code, payload.newStatus));
        const [error]: any = data;

        if (error) {
            handleError(error);
        } else if (payload.code === 'notice') {
            this.shippingOptions.notice.selected = payload.newStatus;
        } else if (payload.code === 'skip') {
            this.shippingOptions.skip.selected = payload.newStatus;
        }

        cart.flagLoading({ key: 'totals', value: false });
        await cart.getTotals();
        await this.estimateShipping(this.shippingAddress);
        await this.getPaymentMethods(
            this.billingSameAsShipping ? this.shippingAddress : this.billingAddress,
            this.shippingAddress
        );
    }

    @Action
    public unlockStep(step: string) {
        this.flagStep({ flag: true, type: 'u', stepName: step });
    }

    @Action
    public lockStep(step: string) {
        this.flagStep({ flag: false, type: 'u', stepName: step });
    }

    @Action
    public activateStep(step: string) {
        this.flagStep({ flag: true, type: 'a', stepName: step });
    }

    @Action
    public deactivateStep(step: string) {
        this.flagStep({ flag: false, type: 'a', stepName: step });
    }

    @Action({ rawError: true })
    public async orderAlreadyPlaced(): Promise<string> {
        return new Promise((resolve) => {
            this.flagOrderPlaced(true);
            resolve(this.orderId.toString());
        });
    }

    @Action({ rawError: true })
    public async placeOrder(request: PaymentInformationRequest): Promise<string> {
        this.flagPlacingOrder(true);
        const data = await to(setPaymentInformation(request));
        const [error]: any = data;
        let [, orderId]: any = data;

        if (error) {
            orderId = 0;
        }

        this.setOrderId(orderId);

        return new Promise((resolve, reject) => {
            if (error) {
                this.flagPlacingOrder(false);
                reject(error);
            } else {
                this.flagOrderPlaced(true);
                resolve(orderId);
            }
        });
    }

    @Action({ rawError: true })
    public async placeAndCreateOrder(request: PaymentInformationRequest): Promise<string> {
        this.flagPlacingOrder(true);
        const [error, orderData]: any = await to(createMagentoOrder(request));

        return new Promise((resolve, reject) => {
            if (error) {
                this.flagPlacingOrder(false);
                reject(error);
            } else {
                this.setOrderId(orderData.id);
                this.flagOrderPlaced(true);
                resolve(orderData);
            }
        });
    }

    @Action
    public async estimateShipping(address: QuoteAddress | null) {
        let shippingMethods: ShippingMethod[] | undefined;
        let error;

        if (!cart.isVirtual) {
            const addressReq: QuoteAddress = address || initState('address');
            const req: EstimateShippingRequest = { address: removeError(normalizeQuoteAddress(addressReq)) };

            if (this.selectedShippingCountry !== null) {
                req.address.country_id = this.selectedShippingCountry;
            }
            if (this.estimateAddress !== null) {
                req.address.postcode = this.estimateAddress.postcode;
            }

            [error, shippingMethods] = await to(estimateShipping(req));

            if (error) {
                shippingMethods = [];
                handleError(error);
            }

            if (shippingMethods && this.selectedShippingMethod) {
                const foundSelectedShippingMethod = shippingMethods.find((m) => {
                    return encodeShipment(m) === encodeShipment(this.selectedShippingMethod);
                });

                if (!foundSelectedShippingMethod) {
                    // Reset selected shipment if it no longer exists in shipping method list
                    this.setSelectedShipment('');
                }
            }
        } else {
            // If only giftcard in cart, return no shipping methods and remove all shipping options (notice, skip, etc.)
            shippingMethods = [];
            const [, resp]: any = await to(removeShippingOptions());

            if (resp) {
                this.shippingOptions.notice.selected = false;
                this.shippingOptions.skip.selected = false;
            }
        }

        this.setShippingMethods(shippingMethods as ShippingMethod[]);
        if (!cart.isEmpty) {
            this.checkSingleShippingMethod();
        }
    }

    @MutationAction({ mutate: ['paymentMethods'] })
    public async getPaymentMethods(billingAddress: QuoteAddress, shippingAddress: QuoteAddress) {
        let paymentMethods: PaymentMethod[] | undefined;
        let billingCountryCode = '';
        if (billingAddress && billingAddress.country_id) {
            billingCountryCode = billingAddress.country_id;
        } else if (shippingAddress && shippingAddress.country_id) {
            billingCountryCode = shippingAddress.country_id;
        }

        const data = await to(getPaymentMethods(billingCountryCode));
        const [error]: any = data;
        // eslint-disable-next-line prefer-destructuring
        paymentMethods = data[1];

        if (error) {
            paymentMethods = [];
            handleError(error);
        }

        if (paymentMethods && store.state.options.payment && store.state.options.payment.costs) {
            const paymentCosts = store.state.options.payment.costs;
            paymentMethods.forEach((method: PaymentMethod) => {
                // eslint-disable-next-line no-prototype-builtins
                if (paymentCosts.hasOwnProperty(method.code)) {
                    method.cost = paymentCosts[method.code];
                }
            });
        }

        if (paymentMethods && store.state.options.payment.withelisted) {
            paymentMethods = paymentMethods.filter((method) => {
                return store.state.options.payment.withelisted.includes(method.code);
            });
        }

        // Initialize payment method if not already done
        if (!this.selectedPayment) {
            this.selectedPayment = initState('selectedPayment');
        }

        // If payment method is set but no longer present in the list reset it
        if (paymentMethods) {
            if (this.selectedPayment && !paymentMethods.find((method) => method.code === this.selectedPayment)) {
                this.selectedPayment = '';
            }
        }

        return { paymentMethods };
    }

    @MutationAction({ mutate: ['coupon'] })
    public async getCoupon() {
        let applied;
        const data = await to(getCoupon());
        const [error]: any = data;
        let [, couponCode]: any = data;

        if (error) {
            applied = false;
            couponCode = '';
            handleError(error, false);
        } else {
            applied = couponCode.length > 0;
        }

        return { coupon: { coupon: couponCode, applied } };
    }

    @Action
    public async applyCoupon(couponCode: string) {
        let applied;
        let couponerr = '';
        const [error, applyRes]: any = await to(doApplyCoupon(couponCode));

        if (error) {
            applied = false;
            // eslint-disable-next-line no-param-reassign
            couponCode = '';
            couponerr = handleError(error, false).message;
        } else {
            applied = applyRes;
        }

        this.setCoupon({ coupon: couponCode, applied: applied as boolean, error: couponerr });
        cart.getTotals();
        this.estimateShipping(this.shippingAddress);
        this.getPaymentMethods(
            this.billingSameAsShipping ? this.shippingAddress : this.billingAddress,
            this.shippingAddress
        );
    }

    @Action
    public async removeCoupon() {
        let applied;
        const [error, applyRes]: any = await to(deleteCoupon());

        if (error) {
            applied = false;
            handleError(error);
        } else {
            applied = !applyRes;
        }

        this.setCoupon({ coupon: '', applied: applied as boolean, error: '' });
        cart.getTotals();
        this.estimateShipping(this.shippingAddress);
        this.getPaymentMethods(
            this.billingSameAsShipping ? this.shippingAddress : this.billingAddress,
            this.shippingAddress
        );
    }

    @MutationAction({ mutate: ['shippingMethods'] })
    public async getShippingMethods() {
        const data = await to(getShippingMethods());
        const [error]: any = data;
        let [, shippingMethods]: any = data;

        if (error) {
            shippingMethods = [];
            handleError(error);
        }

        return { shippingMethods };
    }

    @MutationAction({ mutate: ['selectedPayment'] })
    public async getSelectedPayment() {
        const data = await to(getSelectedPaymentMethod());
        const [error]: any = data;
        let [, selectedPayment]: any = data;

        if (error) {
            selectedPayment = '';
            handleError(error);
        }

        return { selectedPayment };
    }

    @Action
    public async setPayment(paymentCode: string) {
        const request: UpdatePaymentRequest = {
            method: { method: paymentCode },
        };

        this.setSelectedPayment(paymentCode);
        cart.flagLoading({ key: 'totals', value: true });
        const data = await to(setPaymentMethod(request));
        const [error]: any = data;
        let [, selectedPayment]: any = data;

        if (error) {
            selectedPayment = '';
            this.setSelectedPayment(selectedPayment);
            handleError(error);
        }

        const shippingData: ShippingInfoData = {
            address: this.shippingAddress,
            shippingmethod: decodeShipment(this.selectedShipment),
        };

        cart.flagLoading({ key: 'totals', value: false });
        this.setShippingInfo(shippingData);

        return { selectedPayment };
    }

    @Action
    public async assignShippingAddress(address: QuoteAddress) {
        const request: AssignAddressRequest = {
            address,
            useForShipping: true,
            cartId: cart.cartToken.toString() !== '' ? cart.cartToken : cart.cart.id,
        };

        const [error]: any = await to(assignAddress(request));

        return new Promise((resolve) => {
            if (error) {
                handleError(error);
            }

            resolve('');
            this.setShippingAddress(address);
        });
    }

    @Action({ rawError: true })
    public async setShippingInfo(shipInfo: ShippingInfoData) {
        const data = {
            addressInformation: {
                shipping_address: normalizeQuoteAddress(shipInfo.address),
                billing_address: normalizeQuoteAddress(this.billingAddress),
                shipping_method_code: shipInfo.shippingmethod.method_code,
                shipping_carrier_code: shipInfo.shippingmethod.carrier_code,
                extension_attributes: {},
            },
        };

        let resp: any;
        let error: any;
        const previousShippingAmount = cart.totals.shipping_incl_tax;
        cart.flagLoading({ key: 'totals', value: true });
        // eslint-disable-next-line prefer-const
        [error, resp] = await to(setShippingInformation(data));

        if (error) {
            this.setSelectedShipment('');
            await loyalty.resetQuoteBenefits();
            redirectToLocation('/checkout/cart');
        } else {
            if (shipInfo.removeOptions) {
                [, resp] = await to(removeShippingOptions());
                if (resp) {
                    this.shippingOptions.notice.selected = false;
                    this.shippingOptions.skip.selected = false;
                }
            }

            if (resp.payment_methods) {
                this.setPaymentMethods(resp.payment_methods);
            }

            await cart.getTotals();
            await to(this.estimateShipping(data.addressInformation.shipping_address));

            if (previousShippingAmount !== cart.totals.shipping_incl_tax) {
                err(i18n.t('Attention, the cost of shipments has been updated').toString());
            }
        }

        cart.flagLoading({ key: 'totals', value: false });
    }

    @Action
    public async socialLogin(social: string) {
        const socialConvert = social.toLowerCase();
        try {
            await socialLogin(socialConvert);
        } catch (e) {
            handleError(e);
        }
    }

    @Action
    public async updateRequestInvoice(payload: any) {
        const { flag } = payload;
        const params = payload.params || {};
        this.setRequestInvoice(flag);
        if (typeof params.novatrequested !== 'undefined') {
            this.setRequestInvoiceNovat(params.novatrequested);
        }
        cart.flagLoading({ key: 'totals', value: true });
        const [error, res]: any = await to(updateRequestInvoice(flag, params));
        return new Promise((resolve) => {
            if (error) {
                handleError(error);
            }
            this.getPaymentMethods(
                this.billingSameAsShipping ? this.shippingAddress : this.billingAddress,
                this.shippingAddress
            );
            cart.setTotals(res);
            cart.flagLoading({ key: 'totals', value: false });
            resolve('');
        });
    }

    @Action({ rawError: true })
    public async checkSingleShippingMethod(init: boolean = false) {
        if (this.selectedShipment) {
            if (this.shippingMethods && !this.selectedShippingMethod) {
                // If the currently selected shipping method does not exist anymore, reset selected shipment
                this.setSelectedShipment('');
            }
        }
        if (this.selectedShipment === '' || !this.selectedShipment) {
            if (this.shippingMethods.length === 0 && !cart.isVirtual && !init && !cart.isEmpty) {
                handleMessage(
                    'error',
                    i18n.t(`There aren't shipping methods for this address, insert/select another one`).toString()
                );
            } else if (this.shippingMethods.length === 1) {
                if (!init) {
                    err(i18n.t('Attention, the cost of shipments has been updated').toString());
                }
                const [method] = this.shippingMethods;
                const shippingAddress = this.shippingAddress.lastname
                    ? this.shippingAddress
                    : this.defaultShippingAddress;
                const data: ShippingInfoData = {
                    address: shippingAddress,
                    shippingmethod: method,
                };

                this.setSelectedShipment(encodeShipment(method));
                cart.flagLoading({ key: 'totals', value: true });
                await this.setShippingInfo(data)
                    .then(() => {
                        this.getPaymentMethods(
                            this.billingSameAsShipping ? this.shippingAddress : this.billingAddress,
                            this.shippingAddress,
                        ).finally(() => {
                            cart.flagLoading({ key: 'totals', value: false });
                        });
                    })
                    .catch(() => {
                        handleError(err);
                        cart.flagLoading({ key: 'totals', value: false });
                    });
            } else if (cart.isVirtual) {
                this.setSelectedShipment('');
                // redirectToLocation('/checkout/invoice');
            } else if (this.currentStep.name !== 'login') {
                handleMessage('error', i18n.t('Select shipping method').toString());
                redirectToLocation('/checkout/cart');
            }
        }
    }
}

export default getModule(CheckoutModule);
